import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Dropdown } from 'antd';
import { DownOutlined, ArrowLeftOutlined } from '@ant-design/icons';

import SeeEligiblePackages from 'leash/components/seeEligiblePackages/SeeEligiblePackages';
import { useDetailsState } from 'leash/pages/details/useDetailsState';
import {
  isCreated,
  isFinalized,
  isRejected,
} from 'leash/pages/details/utilities/details.utilities';
import { LeashRoutes } from 'leash/utilities/const';
import colors from 'theme/colors';
import { reactEnvironmentVariables } from 'utilities/environmentVariables/environmentVariables';
import { Nullable } from 'types';

import HealthDocumentStatus from 'leash/components/healthDocumentStatus/HealthDocumentStatus';
import { BadgeStyled, PageHeaderStyled } from './DetailsHeader.styles';
import { ManualActionsMenu } from './ManualActionsMenu';

export const badgeColor = (status: Nullable<string>) => {
  if (status?.toLowerCase().includes('rejected')) {
    return colors.alert;
  }

  if (status?.toLowerCase().includes('finalized')) {
    return colors.success;
  }

  if (status?.toLowerCase().includes('created')) {
    return colors.primary;
  }

  return colors.lightGray;
};

interface Props {
  readOnly?: boolean;
}

export function DetailsHeader({ readOnly }: Props): ReactElement | null {
  const { state } = useDetailsState();
  const travelPlan = state?.travelPlan;
  if (!travelPlan) return null;

  const { travelPackage, puppy } = travelPlan;
  const { workflowStatus, travelType } = travelPackage;
  const title = `ID: ${travelPlan?.leadId} ${travelType}`;
  const kcrmLink = `${reactEnvironmentVariables.KCRM_URL}${travelPlan.customer.kustomerId}`;
  const history = useHistory();

  return (
    <PageHeaderStyled
      title={
        <>
          <span>
            <ArrowLeftOutlined onClick={() => history.push(LeashRoutes.Packages)} />
            &nbsp;&nbsp;
            {title}
          </span>
          {(isRejected(workflowStatus) ||
            isFinalized(workflowStatus) ||
            isCreated(workflowStatus)) && (
            <BadgeStyled
              count={workflowStatus}
              statuscolor={badgeColor(workflowStatus)}
              data-testid="travel-workflow-status-badge"
            />
          )}
          <SeeEligiblePackages
            travelEligibility={state.travelEligibility}
            travelTypeId={travelPackage.travelTypeId}
          />
        </>
      }
      extra={
        <>
          <HealthDocumentStatus documents={puppy.documents ?? []} />
          <a href={kcrmLink} target="_blank" rel="noreferrer">
            Go to KCRM
          </a>
          <Dropdown overlay={<ManualActionsMenu />} disabled={readOnly}>
            <Button type="primary" ghost data-testid="manual-actions">
              Manual actions <DownOutlined />
            </Button>
          </Dropdown>
        </>
      }
    />
  );
}
